import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, Form, Card, Spinner, Container, Alert, Row, Col } from 'react-bootstrap';
import { createSliderInstance } from '../../../Models/Slider';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { faTrash, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';
import { LanguageService } from '../../../Services/Axios/Api';
import LanguageSelector from '../../../Components/LanguageSelector/LanguageSelector';
import './SliderBuilder.css';
import { availableAnimations } from '../../../Services/Animations/availableAnimations';

const SliderBuilder = ({id}) => {
    const [sliderInstance] = useState(createSliderInstance());
    const [slides, setSlides] = useState([]);
    const [currentStyle, setCurrentStyle] = useState({});
    const [loading, setLoading] = useState(false);
    const [defaultMessage, setDefaultMessage] = useState(false);
    
    // Language states - only used when id is null
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);   
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);
    
    // Media selector states
    const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
    const [currentSlideForMedia, setCurrentSlideForMedia] = useState(null);
    
    // Color picker states
    const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);
    const [showCaptionColorPicker, setShowCaptionColorPicker] = useState(false);
    // Animation states
    const [animation, setAnimation] = useState(sliderInstance.getAnimation());
    // Visibility state
    const [isVisible, setIsVisible] = useState(sliderInstance.getIsVisible());
    const [containerType, setContainerType] = useState(sliderInstance.getContainerType());

    // New slide form states
    const [newSlide, setNewSlide] = useState({
        title: '',
        caption: '',
        media: '', // Changed from 'image' to 'media' to be more generic
        mediaType: 'image', // 'image' or 'video'
        buttons: []
    });

    // Button form states
    const [newButton, setNewButton] = useState({
        text: '',
        link: '',
        buttonType: 'primary'
    });

    // Validation states
    const [validated, setValidated] = useState(false);
    const [errors, setErrors] = useState({
        newButton: {
            text: false,
            link: false
        }
    });

    // Utility function to check if file is video
    const isVideoFile = (filePath) => {
        return /\.(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(filePath);
    };

    // Utility function to check if file is image
    const isImageFile = (filePath) => {
        return /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tiff|tif)$/i.test(filePath);
    };

    // Utility function to get media type from URL/path
    const getMediaType = (filePath) => {
        if (!filePath || typeof filePath !== 'string') return 'image';
        if (isVideoFile(filePath)) return 'video';
        if (isImageFile(filePath)) return 'image';
        return 'embed';
    };

    useEffect(() => {
        if (id) {
            // If ID exists, load directly without language management
            getData();
        } else {
            // If no ID, load languages first
            loadLanguages();
        }
    }, [id]);

    // Load data after languages are loaded and current language is set (only when no ID)
    useEffect(() => {
        if (!id && isLanguagesLoaded && currentLanguage) {
            getData();
        }
    }, [isLanguagesLoaded, currentLanguage, id]);

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
            
            // Set default language from API
            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
            
            console.log('SliderBuilder: Language loaded:', defaultLanguage);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    };

    const getData = async () => {
        try {
            setLoading(true);
            if (id) {
                // Load by ID first (priority)
                console.log('SliderBuilder: Loading data for ID:', id);
                var data = await sliderInstance.load(id);
                if(Object.keys(data).length === 0){
                    setDefaultMessage(true);
                }
            } else {
                // Load by language when no ID
                console.log('SliderBuilder: Loading data for language:', currentLanguage);
                await sliderInstance.loadByLanguage(currentLanguage);
            }
            

            if(Object.keys(sliderInstance.getSlides()).length > 0){
                const slidesData = sliderInstance.getSlides();
                // Migrate old slides that might have 'image' property to 'media'
                const migratedSlides = slidesData.map(slide => {
                    if (slide.image && !slide.media) {
                        return {
                            ...slide,
                            media: slide.image,
                            mediaType: getMediaType(slide.image)
                        };
                    }
                    return slide;
                });
                setSlides(migratedSlides);
            }
            if(Object.keys(sliderInstance.getStyle()).length > 0){
                setCurrentStyle({...sliderInstance.getStyle()});
            }
            setAnimation(sliderInstance.getAnimation());
            setIsVisible(sliderInstance.getIsVisible());
            setContainerType(sliderInstance.getContainerType());
        } catch (error) {
            console.error('Error loading slider data:', error);
            toast.error('Failed to load slider data');
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        // getData will be called automatically by the useEffect when currentLanguage changes
    };

    const validateForm = (formData, type) => {
        const newErrors = { ...errors };
        let isValid = true;

        if (type === 'newButton') {
            newErrors.newButton = {
                text: !formData.text,
                link: !formData.link
            };
            isValid = !newErrors.newButton.text && !newErrors.newButton.link;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleTitleColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            SlideTitle: {
                ...currentStyle.SlideTitle,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        sliderInstance.setStyle(updatedStyle);
    };

    const handleCaptionColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            SlideCaption: {
                ...currentStyle.SlideCaption,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        sliderInstance.setStyle(updatedStyle);
    };

    const handleVisibilityToggle = () => {
        sliderInstance.toggleVisibility();
        setIsVisible(sliderInstance.getIsVisible());
    };

    const handleContainerTypeChange = (event) => {
        const type = event.target.value;
        sliderInstance.setContainerType(type);
        setContainerType(type);
    };

    const handleMediaSelect = (selectedPath) => {
        if (selectedPath) {
            const mediaType = getMediaType(selectedPath);
            
            if (currentSlideForMedia === 'new') {
                setNewSlide({ 
                    ...newSlide, 
                    media: selectedPath,
                    mediaType: mediaType
                });
            } else if (currentSlideForMedia) {
                const updatedSlides = slides.map(slide => {
                    if (slide.id === currentSlideForMedia) {
                        return { 
                            ...slide, 
                            media: selectedPath,
                            mediaType: mediaType
                        };
                    }
                    return slide;
                });
                setSlides(updatedSlides);
                sliderInstance.setSlides(updatedSlides);
            }
        }
        setIsMediaSelectorOpen(false);
        setCurrentSlideForMedia(null);
    };

    const addNewSlide = (event) => {
        event.preventDefault();
        
        if (!newSlide.media) {
            toast.error("Media (image or video) is required");
            return;
        }

        const slide = {
            id: Date.now(),
            title: newSlide.title,
            caption: newSlide.caption,
            media: newSlide.media,
            mediaType: newSlide.mediaType,
            buttons: newSlide.buttons,
            order: slides.length + 1
        };

        sliderInstance.addSlide(slide);
        setSlides([...sliderInstance.getSlides()]);
        setNewSlide({
            title: '',
            caption: '',
            media: '',
            mediaType: 'image',
            buttons: []
        });
        setValidated(false);
    };

    const addButtonToSlide = (slideId, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        
        if (!validateForm(newButton, 'newButton')) {
            return;
        }

        const button = {
            id: Date.now(),
            type: "button",
            button: {
                text: newButton.text,
                link: newButton.link,
                type: 'link',
                buttonType: newButton.buttonType,
                className: 'SliderBtn',
                style: {}
            }
        };

        const updatedSlides = slides.map(slide => {
            if (slide.id === slideId) {
                return {
                    ...slide,
                    buttons: [...(slide.buttons || []), button]
                };
            }
            return slide;
        });

        setSlides(updatedSlides);
        sliderInstance.setSlides(updatedSlides);
        setNewButton({
            text: '',
            link: '',
            buttonType: 'primary'
        });
        setErrors({
            ...errors,
            newButton: {
                text: false,
                link: false
            }
        });
    };

    const updateSlide = (slideId, updatedData) => {
        const updatedSlides = slides.map(slide => {
            if (slide.id === slideId) {
                const updatedSlide = {
                    ...slide,
                    ...updatedData
                };
                // Update the slider instance immediately
                sliderInstance.updateSlide(slideId, updatedSlide);
                return updatedSlide;
            }
            return slide;
        });
        
        setSlides(updatedSlides);
    };

    const removeSlide = (slideId) => {
        sliderInstance.removeSlide(slideId);
        setSlides(slides.filter(slide => slide.id !== slideId));
    };

    const removeButton = (slideId, buttonId) => {
        const updatedSlides = slides.map(slide => {
            if (slide.id === slideId) {
                return {
                    ...slide,
                    buttons: slide.buttons.filter(btn => btn.id !== buttonId)
                };
            }
            return slide;
        });
        setSlides(updatedSlides);
        sliderInstance.setSlides(updatedSlides);
    };

    const handleSaveChanges = async () => {
        if (slides.length === 0) {
            toast.error("Please add at least one slide");
            return;
        }

        try {
            setLoading(true);
            // Ensure we're saving the current state
            await sliderInstance.setSlides(slides);
            await sliderInstance.setAnimation(animation);
            await sliderInstance.setContainerType(containerType);
            await sliderInstance.save();
            toast.success("Changes saved successfully!");
            await getData();
        } catch (error) {
            toast.error("Failed to save changes. Please try again.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <div className="container mt-4">
            <ToastContainer/>
            {defaultMessage &&
                <Alert variant="info" className="mt-3" dismissible>A default data has been loaded, Happy Building!</Alert>
            }
            <h3 className="mb-3">Slider Builder</h3>
            
            {/* Language Management Card - Only show when no ID */}
            {!id && (
                <Card className="mb-4 language-management-card">
                    <Card.Header className="language-management-header">
                        <Row className="">
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-3">
                                    <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                    <div>
                                        <h6 className="mb-0">Language Management</h6>
                                        <small className="text-muted">
                                            Managing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                        </small>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6} className="d-flex justify-content-end">
                                <LanguageSelector
                                    currentLanguage={currentLanguage}
                                    availableLanguages={availableLanguages}
                                    onLanguageChange={handleLanguageChange}
                                    showBadge={false}
                                    showCopyFromLanguage={false}
                                />
                            </Col>
                        </Row>
                    </Card.Header>
                    
                    {/* Language Alert */}
                    {showLanguageAlert && (
                        <Card.Body className="p-3 language-alert-container">
                            <Alert
                                variant={languageAlertType} 
                                dismissible 
                                onClose={() => setShowLanguageAlert(false)}
                                className="mb-0"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faGlobe} />
                                    <div>
                                        {languageAlertType === 'warning' ? (
                                            <>
                                                <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                                {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                            </>
                                        ) : (
                                            <>
                                                <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this page.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Alert>
                        </Card.Body>
                    )}
                </Card>
            )}

            <Row className="mb-4">
                <Col md={12} className="d-flex justify-content-end align-items-center">
                    <Form.Check
                        type="switch"
                        id="slider-visibility-switch"
                        label="Show Slider"
                        checked={isVisible}
                        onChange={handleVisibilityToggle}
                    />
                </Col>
            </Row>

            <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Add New Slide</Accordion.Header>
                    <Accordion.Body>
                        <Form noValidate onSubmit={addNewSlide} className="slide-form-section">
                            <Alert variant="info" className="mb-3">
                                <strong>🎥 Media Support:</strong> You can now add both images and videos to your slides. 
                                Supported video formats: MP4, MOV, WebM, AVI, MKV, M4V, OGG, WMV
                            </Alert>
                            <Form.Group className="mb-3">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newSlide.title || ''}
                                    onChange={(e) => {
                                        setNewSlide(prev => ({
                                            ...prev,
                                            title: e.target.value
                                        }));
                                    }}
                                    placeholder="Enter slide title"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Caption</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    value={newSlide.caption || ''}
                                    onChange={(e) => {
                                        setNewSlide(prev => ({
                                            ...prev,
                                            caption: e.target.value
                                        }));
                                    }}
                                    placeholder="Enter slide caption"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Media (Image or Video) <span className="text-danger">*</span></Form.Label>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    {newSlide.media ? (
                                        <div className="media-preview-container">
                                            {newSlide.mediaType === 'video' ? (
                                                <video
                                                    src={newSlide.media}
                                                    className="video-preview"
                                                    muted
                                                />
                                            ) : newSlide.mediaType === 'image' ? (
                                                <img 
                                                    src={newSlide.media} 
                                                    className="image-preview"
                                                    alt="Slide media"
                                                />
                                            ) : (
                                                <iframe
                                                    src={newSlide.media}
                                                    className="image-preview"
                                                    style={{ border: 'none' }}
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                    allowFullScreen
                                                    loading="lazy"
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    title="slide-embed-preview"
                                                />
                                            )}
                                            <span className={`media-type-badge ${newSlide.mediaType === 'video' ? 'bg-danger' : newSlide.mediaType === 'image' ? 'bg-primary' : 'bg-secondary'}`}>
                                                {newSlide.mediaType === 'video' ? '🎥' : newSlide.mediaType === 'image' ? '🖼️' : '🌐'}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="media-placeholder" style={{ width: '100px', height: '60px' }}>
                                            No media
                                        </div>
                                    )}
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        className="media-selector-btn"
                                        onClick={() => {
                                            setCurrentSlideForMedia('new');
                                            setIsMediaSelectorOpen(true);
                                        }}
                                    >
                                        Select Media
                                    </Button>
                                </div>
                                {newSlide.media && (
                                    <div className="media-info">
                                        Selected: {newSlide.mediaType === 'video' ? 'Video' : 'Image'} - {newSlide.media.split('/').pop()}
                                    </div>
                                )}
                            </Form.Group>
                            <Button type="submit">Add Slide</Button>
                        </Form>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>Style Settings</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <label className="form-label">Title Color</label>
                            <div className="d-flex align-items-center">
                                <div
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.SlideTitle?.color || '#000000',
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowTitleColorPicker(!showTitleColorPicker)}
                                />
                                <span>{currentStyle.SlideTitle?.color || '#000000'}</span>
                            </div>
                            {showTitleColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowTitleColorPicker(false)}
                                    />
                                    <SketchPicker
                                        color={currentStyle.SlideTitle?.color || '#000000'}
                                        onChange={handleTitleColorChange}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Caption Color</label>
                            <div className="d-flex align-items-center">
                                <div
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.SlideCaption?.color || '#000000',
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowCaptionColorPicker(!showCaptionColorPicker)}
                                />
                                <span>{currentStyle.SlideCaption?.color || '#000000'}</span>
                            </div>
                            {showCaptionColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowCaptionColorPicker(false)}
                                    />
                                    <SketchPicker
                                        color={currentStyle.SlideCaption?.color || '#000000'}
                                        onChange={handleCaptionColorChange}
                                    />
                                </div>
                            )}
                        </div>

                        <Form.Group className="mb-3" controlId="slider-container-type">
                            <Form.Label>Container Width</Form.Label>
                            <Form.Select value={containerType} onChange={handleContainerTypeChange}>
                                <option value="container">Standard Container</option>
                                <option value="container-fluid">Full Width</option>
                            </Form.Select>
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>Animation</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <Form.Label>Animation Type</Form.Label>
                            <Form.Select 
                                value={animation.animationType}
                                onChange={(e) => {
                                    const updated = { ...animation, animationType: e.target.value };
                                    setAnimation(updated);
                                    sliderInstance.setAnimation(updated);
                                }}
                            >
                                {availableAnimations.map((animation) => (
                                    <option value={animation}>{animation}</option>
                                ))}
                                
                            </Form.Select>
                        </div>
                        <div className="mb-3">
                            <Form.Label>Duration (ms)</Form.Label>
                            <Form.Control 
                                type="number" 
                                min="0" 
                                value={animation.animationDuration}
                                onChange={(e) => {
                                    const updated = { ...animation, animationDuration: e.target.value };
                                    setAnimation(updated);
                                    sliderInstance.setAnimation(updated);
                                }}
                            />
                        </div>
                        <div className="mb-3">
                            <Form.Label>Delay (ms)</Form.Label>
                            <Form.Control 
                                type="number" 
                                min="0" 
                                value={animation.animationDelay}
                                onChange={(e) => {
                                    const updated = { ...animation, animationDelay: e.target.value };
                                    setAnimation(updated);
                                    sliderInstance.setAnimation(updated);
                                }}
                            />
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                {slides.map((slide, index) => (
                    <Accordion.Item key={slide.id} eventKey={`slide-${slide.id}`}>
                        <Accordion.Header>Slide {index + 1}</Accordion.Header>
                        <Accordion.Body>
                            <Form className="slide-form-section">
                                <Form.Group className="mb-3">
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={slide.title || ''}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            updateSlide(slide.id, { title: newValue });
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Caption</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        value={slide.caption || ''}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            updateSlide(slide.id, { caption: newValue });
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Media (Image or Video) <span className="text-danger">*</span></Form.Label>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        {slide.media ? (
                                            <div className="media-preview-container">
                                                {slide.mediaType === 'video' ? (
                                                    <video
                                                        src={slide.media}
                                                        className="video-preview"
                                                        muted
                                                    />
                                                ) : slide.mediaType === 'image' ? (
                                                    <img 
                                                        src={slide.media} 
                                                        className="image-preview"
                                                        alt="Slide media"
                                                    />
                                                ) : (
                                                    <iframe
                                                        src={slide.media}
                                                        className="image-preview"
                                                        style={{ border: 'none' }}
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                        allowFullScreen
                                                        loading="lazy"
                                                        referrerPolicy="strict-origin-when-cross-origin"
                                                        title={`slide-embed-${slide.id}`}
                                                    />
                                                )}
                                                <span className={`media-type-badge ${slide.mediaType === 'video' ? 'bg-danger' : slide.mediaType === 'image' ? 'bg-primary' : 'bg-secondary'}`}>
                                                    {slide.mediaType === 'video' ? '🎥' : slide.mediaType === 'image' ? '🖼️' : '🌐'}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="media-placeholder" style={{ width: '100px', height: '60px' }}>
                                                No media
                                            </div>
                                        )}
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm"
                                            className="media-selector-btn"
                                            onClick={() => {
                                                setCurrentSlideForMedia(slide.id);
                                                setIsMediaSelectorOpen(true);
                                            }}
                                        >
                                            Select Media
                                        </Button>
                                    </div>
                                    {slide.media && (
                                        <div className="media-info">
                                            Current: {slide.mediaType === 'video' ? 'Video' : 'Image'} - {slide.media.split('/').pop()}
                                        </div>
                                    )}
                                </Form.Group>

                                <div className="mb-3">
                                    <h5>Buttons</h5>
                                    {slide.buttons?.map((btn) => (
                                        <div key={btn.id} className="mb-2 p-2 border rounded">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>{btn.button.text}</span>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => removeButton(slide.id, btn.id)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}

                                    <div>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Button Text <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                required
                                                type="text"
                                                value={newButton.text}
                                                onChange={(e) => setNewButton({ ...newButton, text: e.target.value })}
                                                placeholder="Button text"
                                                isInvalid={errors.newButton.text}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Button text is required
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Button Link <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                required
                                                type="text"
                                                value={newButton.link}
                                                onChange={(e) => setNewButton({ ...newButton, link: e.target.value })}
                                                placeholder="Button link"
                                                isInvalid={errors.newButton.link}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Button link is required
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Button Type</Form.Label>
                                            <Form.Select
                                                value={newButton.buttonType}
                                                onChange={(e) => setNewButton({ ...newButton, buttonType: e.target.value })}
                                            >
                                                <option value="primary">Primary</option>
                                                <option value="secondary">Secondary</option>
                                            </Form.Select>
                                        </Form.Group>
                                        <Button
                                            variant="primary"
                                            onClick={(e) => addButtonToSlide(slide.id, e)}
                                            disabled={!newButton.text || !newButton.link}
                                        >
                                            Add Button
                                        </Button>
                                    </div>
                                </div>
                                <div className="SaveChangesContainer d-flex justify-content-end mt-3">
                                    <Button
                                        variant="danger"
                                        onClick={() => removeSlide(slide.id)}
                                        className="mt-3"
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                                        Remove Slide
                                    </Button>
                                </div>
                            </Form>
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>

            <div className="SaveChangesContainer d-flex justify-content-end mt-3">
                <ToastContainer />
                <Button 
                    variant="primary" 
                    onClick={handleSaveChanges}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </div>

            <MediaSelector
                isOpen={isMediaSelectorOpen}
                onClose={() => setIsMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={false}
            />
        </div>
    );
};

export default SliderBuilder;